/*
 * Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { memo, useEffect, useState } from 'react';
import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Avatar, Theme, Typography, Icon, IconButton, TableCell } from '@mui/material';
import { TableTooltip } from '../StyledComponents/TableTooltip';
import { useTranslation } from 'react-i18next';
import { MainBtn, MainBtnLight } from '../StyledComponents/MainBtn';
import { RootState } from '../../data/store.typings';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Preloader from '../Preloader/Preloader';
import {
    deleteDelayedDownloadingsItemStartAction,
    downloadFileFromTheListStartAction,
    getDelayedDownloadingsStartAction,
    startDelayedDownloadingsPollingAction,
    stopDelayedDownloadingsPollingAction,
} from '../../data/actions/downloadingsActions';
import createStyles from '@mui/styles/createStyles';
import { createListStyles } from '../RegistryWidgets/Common/ListStyles';
import InnerNotification from '../Notification/InnerNotification';
import { TableContainer, Table } from '@mui/material';
import ListTableHead from '../RegistryWidgets/Common/ListTableHead';
import ListTableBody from '../RegistryWidgets/Common/ListTableBody';
import {
    createTableData,
    delayedDownloadingsListConfig,
    getNumberOfFilesInProgress,
} from './DelayedDownloadingsConfig';
import { ListTableColumnConfig } from '../RegistryWidgets/Common/ListTypes';
import FileManagementCell from './FileManagement/FileManagementCell';
import { theme } from '../../assets/style/them';
import clsx from 'clsx';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import ConfirmDeleteFileModal from './FileManagement/ConfirmDeleteFileModal';
import {
    DelayedDownloadingStatus,
    DelayedDownloadingsListItemDto,
    FileInfo,
} from '../../data/api/downloadings/downloadingsClient.typings';
import { isEqual, xorWith } from 'lodash';
import { RowCheckBox, TableHeadCheckBox } from './FileManagement/CheckBoxes';
import ConfirmDeleteFilesModal from '../CommonModals/ConfirmDeleteFilesModal';
import { CustomStatusCell } from './CustomStatusCell/CustomStatusCell';

const DIALOG_TITLE_HEIGHT = 84.5;
const DIALOG_ACTIONS_HEIGHT = 88;
const TABLE_HEIGHT_SUBTR = DIALOG_TITLE_HEIGHT + DIALOG_ACTIONS_HEIGHT + parseInt(theme.spacing(9));
const MAX_STORAGE_DAYS = 30;
const MAX_STORAGE_ITEMS = 30;

const useStyles = makeStyles((theme: Theme) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    avatar: {
        background: theme.palette.background.paper,
        color: theme.palette.primary.main,
        boxShadow: 'none',
        '&:hover': {
            background: theme.palette.background.paper,
        },
    },
    caption: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginLeft: theme.spacing(1),
    },
    title: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        color: theme.palette.text.secondary,
    },
    titleIcon: {
        fontSize: 28,
        margin: theme.spacing(0, 1, 0, 0),
    },
    titleLabel: {
        fontSize: 20,
        fontWeight: 700,
    },
    total: {
        marginLeft: theme.spacing(1),
        fontSize: 16,
        fontWeight: 500,
    },
    captionButton: {
        maxWidth: 'fit-content',
        minWidth: 180,
        margin: theme.spacing(1, 0),
    },
    deleteCaptionButton: {
        backgroundColor: theme.palette.coral.main,
        '&:hover': {
            background: theme.palette.coral.main,
        },
    },
    tableContainer: {
        maxHeight: `calc(100vh - ${TABLE_HEIGHT_SUBTR}px) !important`,
    },
    dialogActions: {
        margin: theme.spacing(0, 2.5),
        display: 'flex',
        justifyContent: 'space-between',
    },
    limitation: {
        display: 'flex',
        flexDirection: 'column',
        margin: theme.spacing(1, 0, 2, 0),
    },
    actionButton: {
        minWidth: 180,
    },
}));

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        top: 8,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

const DelayedDownloadings: React.FunctionComponent = () => {
    const classes = useStyles();
    const ListStyles = makeStyles((theme: Theme) => createStyles(createListStyles(theme)));
    const listClasses = ListStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const props = useSelector(selectDelayedDownloadingsListProps, shallowEqual);
    const { isLoading, data, isPollingEnabled } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [isOpenDeleteFilesModal, setIsOpenDeleteFilesModal] = useState(false);
    const [deleteFileModalData, setDeleteFileModalData] = useState<{
        fileDto: DelayedDownloadingsListItemDto;
        isOpen: boolean;
    }>({
        fileDto: null,
        isOpen: false,
    });

    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);

    const handleSelect = (fileDto: DelayedDownloadingsListItemDto) => {
        const { id } = fileDto;

        setSelectedIds(xorWith(selectedIds, [id], isEqual));
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked && selectedIds.length === 0) {
            const newSelected = data
                .filter(
                    (item) =>
                        item.status === DelayedDownloadingStatus.Ready ||
                        item.status === DelayedDownloadingStatus.Error,
                )
                .map((item) => item.id);
            setSelectedIds(newSelected);
            return;
        }
        setSelectedIds([]);
    };

    useEffect(() => {
        const requestedIds = data.map((file) => file.id);

        const newSelectedIds = selectedIds.reduce((acc, item) => {
            if (requestedIds.includes(item)) acc.push(item);
            return acc;
        }, []);

        setSelectedIds(newSelectedIds);
    }, [data]);

    useEffect(() => {
        dispatch(getDelayedDownloadingsStartAction());
    }, [dispatch]);

    useEffect(() => {
        if (isPollingEnabled) dispatch(startDelayedDownloadingsPollingAction());
        else dispatch(stopDelayedDownloadingsPollingAction());
    }, [isPollingEnabled, dispatch]);

    const handleClickDelayedDownloadings = () => {
        setIsOpen(true);
        dispatch(getDelayedDownloadingsStartAction());
    };

    const handleCloseModal = () => {
        setIsOpen(false);
    };

    const handleClickUpdate = () => {
        dispatch(getDelayedDownloadingsStartAction());
    };

    const handleDownloadFile = (file: FileInfo) => {
        dispatch(downloadFileFromTheListStartAction({ ...file }));
    };

    const getCustomCellContent = (index: number, columnKey: string, row: number): React.ReactElement => {
        return columnKey === 'selected' ? (
            <RowCheckBox
                key={index}
                fileDto={data[row]}
                selected={selectedIds.indexOf(data[row].id) !== -1}
                onSelect={(fileDto) => handleSelect(fileDto)}
            />
        ) : columnKey === 'management' ? (
            <FileManagementCell
                key={index}
                fileDto={data[row]}
                onDeleteFile={(fileDto) => setDeleteFileModalData({ fileDto, isOpen: true })}
                onDownloadFile={handleDownloadFile}
            />
        ) : columnKey === 'status' ? (
            <CustomStatusCell key={index} item={data[row]} />
        ) : (
            <TableCell />
        );
    };

    const getCustomHeaderCellContent = (index: number, columnKey: string): React.ReactElement => {
        return columnKey === 'selected' ? (
            <TableHeadCheckBox
                key={index}
                numSelected={selectedIds.length}
                onSelectAllClick={handleSelectAllClick}
                rowCount={data.length}
            />
        ) : (
            <TableCell />
        );
    };

    const numberOfFilesInProgress = getNumberOfFilesInProgress([...data]);

    return (
        <div className={classes.wrapper}>
            <Box pr={1.5} pl={1.5}>
                <TableTooltip title={t('downloadings.delayedDownloadings')} placement="left">
                    <IconButton size="small">
                        <StyledBadge badgeContent={numberOfFilesInProgress} color="primary">
                            <Avatar
                                className={classes.avatar}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClickDelayedDownloadings();
                                }}
                            >
                                {numberOfFilesInProgress === 0 ? <CloudDownloadIcon /> : <CloudSyncIcon />}
                            </Avatar>
                        </StyledBadge>
                    </IconButton>
                </TableTooltip>
            </Box>

            <Dialog
                open={isOpen}
                onClose={handleCloseModal}
                aria-labelledby="delayed-downloading-title"
                maxWidth={'md'}
                fullWidth
            >
                <DialogTitle id="delayed-downloading-title">
                    <div className={classes.caption}>
                        <div className={classes.title}>
                            <Icon className={classes.titleIcon}>{'cloud_download'}</Icon>
                            <Typography className={classes.titleLabel} noWrap variant={'subtitle2'}>
                                {t('downloadings.delayedDownloadings')}
                            </Typography>
                            {data && data.length > 0 ? (
                                <Typography className={classes.total} variant={'subtitle2'} noWrap>
                                    {`(${t('app.total')}: ${data.length})`}
                                </Typography>
                            ) : null}
                        </div>
                        <Box mr={2.5}>
                            {selectedIds.length === 0 ? (
                                <MainBtn
                                    className={classes.captionButton}
                                    variant="contained"
                                    size="medium"
                                    disabled={isLoading}
                                    onClick={handleClickUpdate}
                                >
                                    {t('domain.update')}
                                </MainBtn>
                            ) : (
                                <MainBtn
                                    className={clsx(classes.captionButton, classes.deleteCaptionButton)}
                                    variant="contained"
                                    size="medium"
                                    disabled={isLoading}
                                    onClick={() => setIsOpenDeleteFilesModal(true)}
                                >
                                    {`${t('app.delete')} (${selectedIds.length})`}
                                </MainBtn>
                            )}
                        </Box>
                    </div>
                </DialogTitle>
                <DialogContent>
                    {isLoading && (
                        <div className={listClasses.preloaderBox}>
                            <Preloader isInBox isLoading={isLoading} />
                        </div>
                    )}
                    {data && data.length > 0 ? (
                        <TableContainer
                            id="downloads_table"
                            className={clsx(listClasses.tableContainer, classes.tableContainer)}
                        >
                            <Table stickyHeader size="medium">
                                <ListTableHead
                                    columns={delayedDownloadingsListConfig as ListTableColumnConfig<object>[]}
                                    sortingCallback={null}
                                    stateSortParam={''}
                                    getCustomCellContent={getCustomHeaderCellContent}
                                />
                                <ListTableBody
                                    columns={delayedDownloadingsListConfig as ListTableColumnConfig<object>[]}
                                    data={createTableData(data, t)}
                                    getCustomCellContent={getCustomCellContent}
                                />
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box ml={1}>
                            <InnerNotification textTranslatePath={'errors.noResultsFound'} small />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <div className={classes.limitation}>
                        <InnerNotification
                            small
                            textTranslatePath={'downloadings.limitationsHintDays'}
                            textParam={{ limit: MAX_STORAGE_DAYS }}
                            warning
                            noIcon
                        />
                        <InnerNotification
                            small
                            textTranslatePath={'downloadings.limitationsHintItems'}
                            textParam={{ limit: MAX_STORAGE_ITEMS }}
                            warning
                            noIcon
                        />
                    </div>
                    <Box mr={1}>
                        <MainBtnLight
                            variant="outlined"
                            size="medium"
                            className={classes.actionButton}
                            onClick={handleCloseModal}
                        >
                            {t('app.close')}
                        </MainBtnLight>
                    </Box>
                </DialogActions>
            </Dialog>
            <ConfirmDeleteFileModal
                isOpen={deleteFileModalData.isOpen}
                fileDto={deleteFileModalData.fileDto}
                onClose={() => setDeleteFileModalData({ fileDto: null, isOpen: false })}
                onConfirm={() => {
                    dispatch(deleteDelayedDownloadingsItemStartAction([deleteFileModalData.fileDto.id]));
                    setDeleteFileModalData({ fileDto: null, isOpen: false });
                }}
            />
            <ConfirmDeleteFilesModal
                isOpen={isOpenDeleteFilesModal}
                onClose={() => setIsOpenDeleteFilesModal(false)}
                onConfirm={() => {
                    dispatch(deleteDelayedDownloadingsItemStartAction(selectedIds));
                    setIsOpenDeleteFilesModal(false);
                }}
                numberOfFiles={selectedIds.length}
            />
        </div>
    );
};

const selectDelayedDownloadingsListProps = (state: RootState) => {
    return {
        data: state.downloadingsReducer.delayedDownloadingsList,
        isLoading: state.downloadingsReducer.isLoading,
        isPollingEnabled: state.downloadingsReducer.isPollingEnabled,
    };
};

export default memo(DelayedDownloadings);
